import { socket } from '../core/socket.js';
import { state } from '../core/state.js';
import { t } from '../core/i18n.js';
import { popToast } from '../utils/alerts.js';
import { rtcState } from './rtcState.js';
import { audioContext, appelerAgent, arreterScreenShare, detecterVoix } from './peers.js';

export const vocalOnSound  = new Audio('./sounds/sound_vocal_on.mp3');
export const vocalOffSound = new Audio('./sounds/sound_vocal_off.mp3');

// Réveille tous les AudioContext si le navigateur les a suspendus
// (arrière-plan, contrainte mobile gesturielle).
['click', 'touchend'].forEach(ev => document.addEventListener(ev, () => {
    [rtcState._mixCtx, rtcState.rnnoiseContext, audioContext].forEach(c => {
        try { if (c && c.state === 'suspended') c.resume(); } catch (_) {}
    });
}, { passive: true }));

// ==========================================================================
//   PIPELINE AUDIO (RNNoise + mix soundboard)
// ==========================================================================

export async function appliquerSuppressionBruit(rawStream) {
    if (!window.AudioWorklet) {
        console.warn('AudioWorklet non supporté, suppression native uniquement.');
        return rawStream;
    }
    try {
        const wasmBinary   = await fetch('/rnnoise/rnnoise.wasm').then(r => r.arrayBuffer());
        const rnnoiseText  = await fetch('/rnnoise/rnnoise.js').then(r => r.text());
        const patchedRnnoise = rnnoiseText
            .replace('export default createRNNWasmModule;', '')
            .replace(/if\s*\(\s*typeof\s+window\s*!==\s*"undefined"\s*\)\s*window\.createRNNWasmModule\s*=\s*createRNNWasmModule\s*;?/g, '');

        const processorSrc = `
${patchedRnnoise}
const RNN_FRAME = 480;
const CAP = RNN_FRAME * 8;
class RNNoiseProcessor extends AudioWorkletProcessor {
    constructor(options) {
        super();
        this._ready = false;
        this._bypass = options.processorOptions.initialBypass;
        this._inBuf = new Float32Array(CAP);
        this._outBuf = new Float32Array(CAP);
        this._inW = 0; this._inR = 0; this._inN = 0;
        this._outW = 0; this._outR = 0; this._outN = 0;
        this._frame = new Float32Array(RNN_FRAME);
        this._init(options.processorOptions.wasmBinary);
        this.port.onmessage = (e) => {
            if (e.data.type === 'bypass') this._bypass = e.data.value;
        };
    }
    async _init(wasmBinary) {
        try {
            this._rnn = await createRNNWasmModule({ wasmBinary });
            this._state = this._rnn._rnnoise_create(0);
            this._ptr = this._rnn._malloc(RNN_FRAME * 4);
            this._ready = true;
            this.port.postMessage({ type: 'ready' });
        } catch (e) {
            this.port.postMessage({ type: 'error', message: String(e && e.message || e) });
        }
    }
    process(inputs, outputs) {
        const inp = inputs[0] && inputs[0][0];
        const out = outputs[0] && outputs[0][0];
        if (!out) return true;
        if (!this._ready || !inp || this._bypass) {
            if (inp) out.set(inp);
            return true;
        }
        const n = inp.length;
        for (let i = 0; i < n; i++) {
            this._inBuf[this._inW] = inp[i];
            this._inW = (this._inW + 1) % CAP;
            this._inN++;
        }
        while (this._inN >= RNN_FRAME) {
            for (let i = 0; i < RNN_FRAME; i++) {
                this._frame[i] = this._inBuf[this._inR];
                this._inR = (this._inR + 1) % CAP;
                this._inN--;
            }
            const h = this._rnn.HEAPF32, off = this._ptr >> 2;
            for (let i = 0; i < RNN_FRAME; i++) h[off + i] = this._frame[i] * 32768;
            this._rnn._rnnoise_process_frame(this._state, this._ptr, this._ptr);
            for (let i = 0; i < RNN_FRAME; i++) {
                this._outBuf[this._outW] = h[off + i] / 32768;
                this._outW = (this._outW + 1) % CAP;
                this._outN++;
            }
        }
        if (this._outN >= n) {
            for (let i = 0; i < n; i++) {
                out[i] = this._outBuf[this._outR];
                this._outR = (this._outR + 1) % CAP;
                this._outN--;
            }
        } else {
            out.set(inp);
        }
        return true;
    }
}
registerProcessor('rnnoise-processor', RNNoiseProcessor);
`;
        const blob    = new Blob([processorSrc], { type: 'application/javascript' });
        const blobUrl = URL.createObjectURL(blob);

        const ctx = new AudioContext({ sampleRate: 48000 });
        if (ctx.state === 'suspended') { try { await ctx.resume(); } catch (_) {} }
        await ctx.audioWorklet.addModule(blobUrl);
        URL.revokeObjectURL(blobUrl);

        const source      = ctx.createMediaStreamSource(rawStream);
        const worklet     = new AudioWorkletNode(ctx, 'rnnoise-processor', {
            processorOptions: { wasmBinary, initialBypass: !rtcState.suppressionActive }
        });
        const destination = ctx.createMediaStreamDestination();

        worklet.port.onmessage = (e) => {
            if (e.data.type === 'error') console.error('RNNoise worklet error:', e.data.message);
        };

        source.connect(worklet);
        worklet.connect(destination);

        rtcState.rnnoiseContext    = ctx;
        rtcState.rnnoiseWorkletNode = worklet;
        rtcState._mixCtx           = ctx;
        rtcState._mixDestination   = destination;

        return destination.stream;
    } catch (err) {
        console.error('RNNoise init échoué — fallback mix sans suppression :', err);
        try {
            const ctx = new AudioContext({ sampleRate: 48000 });
            if (ctx.state === 'suspended') { try { await ctx.resume(); } catch (_) {} }
            const source      = ctx.createMediaStreamSource(rawStream);
            const destination = ctx.createMediaStreamDestination();
            source.connect(destination);
            rtcState.rnnoiseContext  = ctx;
            rtcState._mixCtx         = ctx;
            rtcState._mixDestination = destination;
            return destination.stream;
        } catch (err2) {
            console.error('Fallback mix impossible, micro brut (soundboard local seulement) :', err2);
            return rawStream;
        }
    }
}

export function stopperFluxAudio() {
    if (rtcState.rnnoiseContext) {
        rtcState.rnnoiseContext.close().catch(() => {});
        rtcState.rnnoiseContext = null;
    }
    rtcState.rnnoiseWorkletNode = null;
    rtcState._mixCtx            = null;
    rtcState._mixDestination    = null;
    const streamBrut = rtcState.fluxAudioBrut || rtcState.fluxAudioLocal;
    if (streamBrut) streamBrut.getTracks().forEach(t => t.stop());
    rtcState.fluxAudioBrut  = null;
    rtcState.fluxAudioLocal = null;
}

// ==========================================================================
//   MICRO & CONTRÔLES VOCAUX
// ==========================================================================

export async function activerMicro() {
    try {
        rtcState.fluxAudioBrut = await navigator.mediaDevices.getUserMedia({
            audio: { echoCancellation: true, noiseSuppression: false, autoGainControl: true },
            video: false
        });
        rtcState.fluxAudioLocal = await appliquerSuppressionBruit(rtcState.fluxAudioBrut);

        popToast(t('toast.mic_connected'));
        vocalOnSound.play().catch(() => {});

        detecterVoix(rtcState.fluxAudioBrut, (parle) => {
            const monAvatar    = document.getElementById('self-avatar');
            const monMiniAvatar = document.getElementById(`mini-avatar-${state.monPseudo}`);
            if (parle && !rtcState.microCoupe) {
                rtcState.speakingNow.add(state.monPseudo);
                if (monAvatar)     monAvatar.classList.add('is-speaking-border');
                if (monMiniAvatar) monMiniAvatar.classList.add('is-speaking-border');
            } else {
                rtcState.speakingNow.delete(state.monPseudo);
                if (monAvatar)     monAvatar.classList.remove('is-speaking-border');
                if (monMiniAvatar) monMiniAvatar.classList.remove('is-speaking-border');
            }
        });

        const salonId = localStorage.getItem('memoireVocalId');
        if (salonId && rtcState.etatsVocauxActuels[salonId]) {
            rtcState.etatsVocauxActuels[salonId].forEach(pseudoAgent => {
                if (pseudoAgent !== state.monPseudo) appelerAgent(pseudoAgent);
            });
        }

        const btnStream = document.getElementById('btn-stream');
        if (btnStream) btnStream.style.display = 'flex';
        // PiP : visible uniquement sous Electron desktop (pas en navigateur).
        const btnPip = document.getElementById('btn-pip');
        if (btnPip && window.chaosNative && window.chaosNative.ouvrirPiP) btnPip.style.display = 'flex';
    } catch (err) {
        console.error("❌ Erreur d'accès au micro :", err);
        popToast(t('toast.mic_denied'), 'error');
        quitterVocal();
    }
}

export function toggleMute() {
    if (!rtcState.fluxAudioLocal) return popToast(t('toast.not_in_vocal_flux'), 'error');

    rtcState.microCoupe = !rtcState.microCoupe;
    const pisteAudio = rtcState.fluxAudioLocal.getAudioTracks()[0];
    if (pisteAudio) pisteAudio.enabled = !rtcState.microCoupe;

    document.querySelectorAll('.orb-btn[title="Micro"]').forEach(btn => {
        if (rtcState.microCoupe) {
            btn.innerHTML = '<i class="fas fa-microphone-slash"></i>';
            btn.style.color = '#f85149';
        } else {
            btn.innerHTML = '<i class="fas fa-microphone"></i>';
            btn.style.color = '#8b949e';
        }
    });
}

export function toggleDeaf() {
    rtcState.sonCoupe = !rtcState.sonCoupe;
    Object.values(rtcState.elementsAudio).forEach(baliseAudio => {
        baliseAudio.muted = rtcState.sonCoupe;
    });
    document.querySelectorAll('.orb-btn[title="Casque"]').forEach(btn => {
        if (rtcState.sonCoupe) {
            btn.innerHTML = '<i class="fas fa-volume-xmark"></i>';
            btn.style.color = '#f85149';
        } else {
            btn.innerHTML = '<i class="fas fa-headphones"></i>';
            btn.style.color = '#8b949e';
        }
    });
}

export function toggleSuppression() {
    rtcState.suppressionActive = !rtcState.suppressionActive;
    localStorage.setItem('chaos_rnnoise', rtcState.suppressionActive);
    if (rtcState.rnnoiseWorkletNode) {
        rtcState.rnnoiseWorkletNode.port.postMessage({ type: 'bypass', value: !rtcState.suppressionActive });
    }
    const btn = document.getElementById('btn-suppression');
    if (btn) {
        btn.classList.toggle('active-suppression', rtcState.suppressionActive);
        btn.title = rtcState.suppressionActive ? t('nav.suppression_on') : t('nav.suppression_off');
    }
    popToast(rtcState.suppressionActive ? t('nav.suppression_on') : t('nav.suppression_off'));
}

// ==========================================================================
//   QUITTER LE VOCAL
// ==========================================================================

export function afficherSelfHangup(handler) {
    const btn = document.getElementById('self-hangup-btn');
    if (!btn) return;
    if (typeof handler === 'function') btn.onclick = handler;
    btn.style.display = 'inline-flex';
}

export function cacherSelfHangup() {
    const btn = document.getElementById('self-hangup-btn');
    if (btn) btn.style.display = 'none';
}

// Supprime le flyout au survol du bouton raccrocher (ne doit s'activer que sur l'orbe).
(function () {
    const sh   = document.getElementById('self-hangup-btn');
    const cont = document.querySelector('.agent-orb-container');
    if (!sh || !cont) return;
    sh.addEventListener('mouseenter', () => cont.classList.add('flyout-suppressed'));
    sh.addEventListener('mouseleave', () => cont.classList.remove('flyout-suppressed'));
})();

export function quitterVocal() {
    fermerPopoverVolume();
    vocalOffSound.play().catch(() => {});

    stopperFluxAudio();
    arreterScreenShare();

    Object.keys(rtcState.connexionsPairs).forEach(pseudo => {
        rtcState.connexionsPairs[pseudo].close();
        delete rtcState.connexionsPairs[pseudo];
    });

    Object.keys(rtcState.elementsAudio).forEach(pseudo => {
        rtcState.elementsAudio[pseudo].remove();
        delete rtcState.elementsAudio[pseudo];
    });
    Object.keys(rtcState.gainsAudio).forEach(pseudo => {
        try { rtcState.gainsAudio[pseudo].ctx.close(); } catch (e) {}
        delete rtcState.gainsAudio[pseudo];
    });

    document.querySelectorAll('.stream-video-element').forEach(el => el.remove());
    document.querySelectorAll('.live-badge').forEach(badge => badge.remove());

    const mpBanner = document.getElementById('mp-call-banner');
    if (mpBanner) mpBanner.style.display = 'none';

    const aura = document.getElementById('vocal-aura');
    if (aura) aura.classList.remove('is-vocal-active');

    const infoPanel = document.getElementById('vocal-info-panel');
    if (infoPanel) infoPanel.style.display = 'none';

    const btnDisconnect = document.getElementById('vocal-disconnect-btn');
    if (btnDisconnect) btnDisconnect.style.display = 'none';
    cacherSelfHangup();

    const btnStream = document.getElementById('btn-stream');
    if (btnStream) btnStream.style.display = 'none';
    const btnPip = document.getElementById('btn-pip');
    if (btnPip) btnPip.style.display = 'none';
    // Si une PiP est ouverte, on la ferme aussi en sortant du vocal.
    if (window.chaosNative && window.chaosNative.fermerPiP) window.chaosNative.fermerPiP();

    const roomNameDisplay = document.getElementById('vocal-active-room-name');
    if (roomNameDisplay) roomNameDisplay.innerText = '...';

    localStorage.removeItem('memoireVocalId');
    localStorage.removeItem('memoireVocalNom');
    localStorage.removeItem('memoireAppelMP');
    majBulleVocalRejoint();

    const sbPanel = document.getElementById('soundboard-panel');
    if (sbPanel) sbPanel.style.display = 'none';

    socket.emit('quitter-vocal', { pseudo: state.monPseudo });
}

export function quitterVocalVisuellement() {
    const aura = document.getElementById('vocal-aura');
    if (aura) aura.classList.remove('is-vocal-active');
    const roomNameDisplay = document.getElementById('vocal-active-room-name');
    if (roomNameDisplay) roomNameDisplay.innerText = t('vocal.disconnected');
}

// ==========================================================================
//   AVATARS VOCAUX & VOLUME PAR UTILISATEUR
// ==========================================================================

export function dessinerAvatarsVocaux() {
    document.querySelectorAll('.vocal-members-list').forEach(list => list.innerHTML = '');

    Object.keys(rtcState.etatsVocauxActuels).forEach(salonId => {
        const members   = rtcState.etatsVocauxActuels[salonId];
        const container = document.getElementById(`vocal-members-${salonId}`);
        if (!container) return;

        members.forEach(p => {
            const tag = document.createElement('div');
            tag.className = 'vocal-agent-tag';

            const infosAuteur = state.utilisateurs[p] || {};
            const urlAvatar   = infosAuteur.avatar_url;
            const userColor   = infosAuteur.couleur || '#ffb75e';

            let avatarHtml = '';
            if (urlAvatar && urlAvatar !== 'none' && String(urlAvatar) !== 'undefined') {
                avatarHtml = `<div id="mini-avatar-${p}" class="mini-avatar-chaos" style="background: url('${urlAvatar}') center/cover no-repeat; border: 2px solid ${userColor}; color: transparent;"></div>`;
            } else {
                avatarHtml = `<div id="mini-avatar-${p}" class="mini-avatar-chaos" style="background-color: ${userColor}">${p.charAt(0).toUpperCase()}</div>`;
            }

            const enStream = rtcState.pseudosEnStream.has(p);
            const streamIcone = enStream
                ? `<span class="stream-badge-icone" title="Partage d'écran actif — cliquez pour regarder"><i class="fas fa-desktop"></i></span>`
                : '';
            tag.innerHTML = `${avatarHtml}<span class="vocal-pseudo">${p}</span>${streamIcone}`;
            tag.dataset.pseudo = p;
            if (enStream) {
                tag.classList.add('is-streaming');
                // Glow derrière le pseudo dans la couleur du thème de l'utilisateur
                const couleur = userColor || '#ffb75e';
                const pseudoSpan = tag.querySelector('.vocal-pseudo');
                if (pseudoSpan) {
                    pseudoSpan.style.background = couleur + '28';
                    pseudoSpan.style.boxShadow = `0 0 8px ${couleur}99`;
                    pseudoSpan.style.borderRadius = '4px';
                    pseudoSpan.style.padding = '1px 5px';
                }
            }

            if (p !== state.monPseudo) {
                tag.addEventListener('contextmenu', (e) => {
                    e.preventDefault();
                    afficherSliderVolume(p, e.clientX, e.clientY);
                });
                let pressTimer = null;
                tag.addEventListener('touchstart', (e) => {
                    const touch = e.touches[0];
                    pressTimer = setTimeout(() => afficherSliderVolume(p, touch.clientX, touch.clientY), 500);
                });
                const cancelPress = () => { if (pressTimer) clearTimeout(pressTimer); };
                tag.addEventListener('touchend',   cancelPress);
                tag.addEventListener('touchmove',  cancelPress);
                tag.addEventListener('touchcancel', cancelPress);
            }

            container.appendChild(tag);

            // Brancher le clic sur l'icône stream si présente
            if (rtcState.pseudosEnStream.has(p)) {
                const icone = tag.querySelector('.stream-badge-icone');
                if (icone) icone.onclick = (e) => {
                    e.stopPropagation();
                    if (typeof window.ouvrirStreamChoisi === 'function') window.ouvrirStreamChoisi(p);
                };
            }
        });
    });
}

export function appliquerVolumeUtilisateur(pseudo, valeur) {
    rtcState.volumesUtilisateurs[pseudo] = valeur;
    localStorage.setItem('chaos_user_volumes', JSON.stringify(rtcState.volumesUtilisateurs));
    if (rtcState.elementsAudio[pseudo]) {
        rtcState.elementsAudio[pseudo].volume = Math.min(1, Math.max(0, valeur / 100));
    }
    if (rtcState.gainsAudio[pseudo] && rtcState.gainsAudio[pseudo].gain) {
        rtcState.gainsAudio[pseudo].gain.gain.value = valeur / 100;
    }
}

let _popoverCleanup = null;

export function fermerPopoverVolume() {
    if (_popoverCleanup) { _popoverCleanup(); }
}

export function afficherSliderVolume(pseudo, x, y) {
    if (_popoverCleanup) _popoverCleanup();

    const currentVol = rtcState.volumesUtilisateurs[pseudo] ?? 100;
    const popover = document.createElement('div');
    popover.className = 'volume-popover';
    popover.style.left = x + 'px';
    popover.style.top  = y + 'px';
    popover.innerHTML = `
        <div class="volume-popover-title">${pseudo}</div>
        <input type="range" class="volume-slider" min="0" max="200" value="${currentVol}">
        <div class="volume-value">${currentVol}%</div>
        <button class="volume-reset" type="button">${t('vocal.volume_reset')}</button>
    `;
    document.body.appendChild(popover);

    const rect = popover.getBoundingClientRect();
    if (rect.right  > window.innerWidth)  popover.style.left = (window.innerWidth  - rect.width  - 10) + 'px';
    if (rect.bottom > window.innerHeight) popover.style.top  = (window.innerHeight - rect.height - 10) + 'px';

    const slider   = popover.querySelector('.volume-slider');
    const valueLbl = popover.querySelector('.volume-value');

    slider.addEventListener('input', () => {
        const v = parseInt(slider.value, 10);
        valueLbl.textContent = v + '%';
        appliquerVolumeUtilisateur(pseudo, v);
    });

    popover.querySelector('.volume-reset').addEventListener('click', (e) => {
        e.stopPropagation();
        slider.value = 100;
        valueLbl.textContent = '100%';
        appliquerVolumeUtilisateur(pseudo, 100);
    });

    const onClickOutside = (e) => {
        if (!popover.contains(e.target)) cleanup();
    };

    function cleanup() {
        popover.remove();
        document.removeEventListener('click', onClickOutside);
        document.removeEventListener('contextmenu', onClickOutside);
        _popoverCleanup = null;
    }

    setTimeout(() => {
        document.addEventListener('click', onClickOutside);
        document.addEventListener('contextmenu', onClickOutside);
    }, 50);

    _popoverCleanup = cleanup;
}

// ==========================================================================
//   INDICATEURS VOCAL (bulle + bouton soundboard header)
// ==========================================================================

export function majBulleVocalRejoint() {
    const joinedId = localStorage.getItem('memoireVocalId');
    document.querySelectorAll('#liste-salons .vocal-chat-bubble').forEach(b => {
        const match = joinedId && String(b.dataset.salonId) === String(joinedId);
        b.style.display = match ? 'inline-flex' : 'none';
    });
    majBoutonSoundboardHeader();
}

export function majBoutonSoundboardHeader() {
    const b = document.getElementById('btn-soundboard-header');
    if (!b) return;
    b.style.display = localStorage.getItem('memoireVocalId') ? 'inline-flex' : 'none';
}

socket.on('vocal-update-global', (allVocalStates) => {
    const monSalon = localStorage.getItem('memoireVocalId');
    if (monSalon && rtcState.etatsVocauxActuels[monSalon]) {
        const avant   = new Set(rtcState.etatsVocauxActuels[monSalon]);
        const apres   = new Set(allVocalStates[monSalon] || []);
        const quelquUnAQuitte = [...avant].some(p => p !== state.monPseudo && !apres.has(p));
        if (quelquUnAQuitte) vocalOffSound.play().catch(() => {});
    }
    rtcState.etatsVocauxActuels = allVocalStates;
    dessinerAvatarsVocaux();
});

socket.on('stream-spectateurs', (liste) => {
    if (typeof window._majSpectateurs === 'function') window._majSpectateurs(liste);
});

// ==========================================================================
//   SOUNDBOARD
// ==========================================================================

export function toggleSoundboard(e) {
    if (e) e.stopPropagation();
    const panel = document.getElementById('soundboard-panel');
    if (!panel) return;
    if (panel.style.display === 'flex') { panel.style.display = 'none'; return; }

    const enMP          = !!localStorage.getItem('memoireAppelMP');
    const enSphereVocal = !!localStorage.getItem('memoireVocalId') && !!state.sphereActuelleID;

    if (enMP) {
        rtcState._sbMode    = 'perso';
        rtcState._sbSphereId = null;
    } else if (enSphereVocal) {
        rtcState._sbMode    = 'sphere';
        rtcState._sbSphereId = state.sphereActuelleID;
    } else {
        popToast("Le soundboard s'utilise pendant un vocal de sphère ou un appel privé.", 'stream');
        return;
    }

    document.getElementById('soundboard-grid').innerHTML = '<p class="sb-empty">Chargement…</p>';
    document.getElementById('sb-msg').textContent = '';
    panel.style.display = 'flex';
    if (rtcState._sbMode === 'perso') socket.emit('lister-sons-perso');
    else socket.emit('lister-sons', { sphereId: rtcState._sbSphereId });
}

socket.on('soundboard-liste', ({ sphereId, sons, peutGerer, perso } = {}) => {
    if (perso) {
        if (rtcState._sbMode !== 'perso') return;
    } else {
        if (rtcState._sbMode !== 'sphere' || rtcState._sbSphereId == null || String(sphereId) !== String(rtcState._sbSphereId)) return;
    }
    rtcState._sbPeutGerer = !!peutGerer;
    const grid   = document.getElementById('soundboard-grid');
    const manage = document.getElementById('soundboard-manage');
    if (manage) manage.style.display = rtcState._sbPeutGerer ? 'block' : 'none';
    if (!grid) return;
    grid.innerHTML = '';
    if (!Array.isArray(sons) || sons.length === 0) {
        grid.innerHTML = '<p class="sb-empty">Aucun son pour l\'instant.</p>';
        return;
    }
    sons.forEach(s => {
        const tile = document.createElement('button');
        tile.type  = 'button';
        tile.className = 'sb-tile';
        tile.title = s.nom;
        const ic  = document.createElement('span'); ic.className = 'sb-tile-ic'; ic.textContent = '🔊';
        const lbl = document.createElement('span'); lbl.className = 'sb-tile-lbl';
        lbl.textContent = s.nom;
        tile.appendChild(ic); tile.appendChild(lbl);
        tile.addEventListener('click', () => jouerSon(s.url, s.nom));
        if (rtcState._sbPeutGerer) {
            const del = document.createElement('span');
            del.className = 'sb-tile-del';
            del.textContent = '×';
            del.title = 'Supprimer';
            del.addEventListener('click', (ev) => {
                ev.stopPropagation();
                socket.emit(rtcState._sbMode === 'perso' ? 'supprimer-son-perso' : 'supprimer-son', { id: s.id });
            });
            tile.appendChild(del);
        }
        grid.appendChild(tile);
    });
});

export async function jouerSon(url, nom) {
    const now = Date.now();
    if (now - rtcState._dernierSonAt < 600) return;
    rtcState._dernierSonAt = now;
    try {
        if (rtcState._mixCtx && rtcState._mixDestination) {
            if (rtcState._mixCtx.state === 'suspended') { try { await rtcState._mixCtx.resume(); } catch (_) {} }
            let buf = rtcState._sonBufferCache[url];
            if (!buf) {
                const ab = await fetch(url).then(r => r.arrayBuffer());
                buf = await rtcState._mixCtx.decodeAudioData(ab);
                rtcState._sonBufferCache[url] = buf;
            }
            const src = rtcState._mixCtx.createBufferSource();
            src.buffer = buf;
            src.connect(rtcState._mixDestination);
            src.connect(rtcState._mixCtx.destination);
            src.start();
            src.onended = () => { try { src.disconnect(); } catch (_) {} };
            if (rtcState._sbSphereId) {
                socket.emit('jouer-son', { sphereId: rtcState._sbSphereId, nom, url });
            } else if (rtcState._sbMode === 'perso' && window.destinataireID) {
                socket.emit('jouer-son-perso', { cible: window.destinataireID, nom, url });
            }
        } else {
            new Audio(url).play().catch(() => {});
        }
    } catch (err) {
        console.error('[Soundboard] Lecture échouée :', err);
        popToast('Lecture du son impossible.', 'error');
    }
}

export async function ajouterSon() {
    const inp      = document.getElementById('sb-fichier');
    const champNom = document.getElementById('sb-nouveau-nom');
    const msg      = document.getElementById('sb-msg');
    const f = inp.files && inp.files[0];
    inp.value = '';
    if (!f || !rtcState._sbMode) return;
    if (rtcState._sbMode === 'sphere' && !rtcState._sbSphereId) return;
    if (f.size > 2 * 1024 * 1024) {
        if (msg) { msg.style.color = '#f85149'; msg.textContent = 'Fichier trop lourd (≤ 2 Mo).'; }
        return;
    }
    const nom = ((champNom.value.trim() || f.name.replace(/\.[^.]+$/, '')) || 'Son').slice(0, 40);
    if (msg) { msg.style.color = '#8b949e'; msg.textContent = 'Envoi…'; }
    try {
        const fd = new FormData();
        fd.append('son', f);
        const res  = await fetch('/api/upload-soundboard', { method: 'POST', body: fd });
        const data = await res.json();
        if (!data.success) {
            if (msg) { msg.style.color = '#f85149'; msg.textContent = data.error || 'Échec upload.'; }
            return;
        }
        if (rtcState._sbMode === 'perso') socket.emit('creer-son-perso', { nom, url: data.url });
        else socket.emit('creer-son', { sphereId: rtcState._sbSphereId, nom, url: data.url });
        champNom.value = '';
        if (msg) { msg.style.color = '#3fb950'; msg.textContent = '✅ Son ajouté.'; }
    } catch (err) {
        console.error('[Soundboard] Upload échoué :', err);
        if (msg) { msg.style.color = '#f85149'; msg.textContent = 'Erreur réseau.'; }
    }
}

socket.on('son-joue', ({ par, nom, url } = {}) => {
    popToast(`🔊 ${par || "Quelqu'un"} a joué « ${nom || 'un son'} »`, 'stream');
    if (url) new Audio(url).play().catch(() => {});
});
